/* eslint-disable no-nested-ternary */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment";
import { PieChart, Pie, Cell, Tooltip } from "recharts";

import { API, fileUpload, get } from "../../../../config";

import { Card, CardContent, CardHeader } from "../../../../components/ui/card";
import { Step, Stepper } from "../../../../components/ui/stepper";
import Spin from "../../../../components/Spin";
import { ResponsiveDrawerForm } from "../../../../components/ResponsiveDrawerForm";
import StepperNavigation from "./StepperNavigation";
import { useOrganization } from "../../../../lib/context/OrganisationContext";
import { formatName } from "../../../../lib/Misc";

const RiskAssessmentDetailsPopup = ({
  id,
  sectionType,
  showView,
  setShowView,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [sections, setSections] = useState([]);
  const [stepsData, setStepsData] = useState([]);
  const [assessmentDate, setAssessmentDate] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [reviewDate, setReviewDate] = useState(null);
  const [assessedBy, setAssessedBy] = useState("");
  const [compliance, setCompliance] = useState("");
  const [analysis, setAnalysis] = useState({});
  const [chartData, setChartData] = useState([]);
  const [needleData, setNeedleData] = useState({});
  const [year, setYear] = useState(null);
  const { organizationData } = useOrganization();

  const questionBodyRef = useRef(null);

  /* ------------------ Fetch Assessment ------------------ */
  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await get(`${API.GET_RISK_ASSESSMENT}/${id}`);
      const sectionData = data?.data || {};
      setCompliance(sectionData?.compliance_manager ? formatName(sectionData?.compliance_manager?.first_name, sectionData?.compliance_manager?.last_name) : "")
      setAssessmentDate(
        sectionData?.assessment_date
          ? moment(sectionData.assessment_date, "DD-MM-YYYY")
          : null
      );
      setReviewDate(
        sectionData?.next_review_date
          ? moment(sectionData.next_review_date, "DD-MM-YYYY")
          : null
      );
      setYear(sectionData?.year ? moment(sectionData.year, "YYYY") : null);

      setAssessedBy(
        sectionData?.user
          ? `${sectionData.user.first_name || ""} ${
              sectionData.user.last_name || ""
            }`
          : ""
      );
      // Example usage
      const transformed = sectionData?.risk_assessment_sections?.map(
        (sectionData) => ({
          id: sectionData?.section_id, // or use section.id if you prefer
          title:
            sectionData?.section?.title ||
            `Section ${sectionData?.section?.id}`,
          sub_title: sectionData?.section?.sub_title
            ? sectionData?.section?.sub_title
            : "",
          yes_color: sectionData?.section?.yes_color
            ? sectionData?.section?.yes_color
            : "#ff0000",
          no_color: sectionData?.section?.no_color
            ? sectionData?.section?.no_color
            : "#007718",
          questions: sectionData?.risk_assessment_section_answers?.map(
            (ans) => ({
              id: ans?.question?.id,
              label: ans?.question?.label,
              question: ans?.question?.question,
              options: ["Yes", "No"],
              answer: ans?.answer === 1 ? "Yes" : "No",
            })
          ),
        })
      );

      setSections(transformed);
      setStepsData([
        ...transformed?.map((section) => ({ label: section.title })),
        { label: "Summary" },
      ]);

      setAnalysis(data.analysis);
    } catch (e) {
      console.error(e);
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ------------------ Conditions ------------------ */
  const fetchConditions = useCallback(async () => {
    const { data } = await get(
      `${API.GET_CONDITIONS}?page=1&limit=-1&section_type=AML`
    );

    const chart = [];
    const needle = {};
    let counter = 0;

    data?.data?.data?.forEach((item) => {
      chart.push({
        name: item.risk_level,
        value: 1,
        color: item.color,
      });
      needle[item.risk_level] = counter + 0.5;
      counter++;
    });

    setChartData(chart);
    setNeedleData(needle);
  }, []);

  useEffect(() => {
    fetchConditions();
  }, [fetchConditions]);

  /* ------------------ Helpers ------------------ */
  const yesCounts = useMemo(
    () =>
      sections.map((s) => s.questions.filter((q) => q.answer === "Yes").length),
    [sections]
  );

  /* ------------------ Gauge ------------------ */
  const cx = 200,
    cy = 200,
    iR = 80,
    oR = 110,
    RADIAN = Math.PI / 180;

  const needle = (value, data) => {
    const total = data.reduce((a, b) => a + b.value, 0);
    const angle = 180 * (1 - value / total);
    const sin = Math.sin(-RADIAN * angle);
    const cos = Math.cos(-RADIAN * angle);

    const r = 5;
    const x0 = cx;
    const y0 = cy;
    const xp = x0 + ((iR + oR) / 2) * cos;
    const yp = y0 + ((iR + oR) / 2) * sin;

    return (
      <>
        <circle cx={x0} cy={y0} r={r} fill="#000" />
        <path d={`M${x0} ${y0} L${xp} ${yp}`} stroke="#000" strokeWidth={2} />
      </>
    );
  };

    const fetchAnalysis = useCallback(
      async (sections) => {
        try {
          setLoadingSummary(true);
          const formData = new FormData();
          formData.append("organization_id", organizationData?.organizationId);
  
          formData.append("section_type", sectionType);
          for (let i = 0; i < sections?.length; i++) {
            let keyName = `section_${sections[i].id}`;
            formData.append(
              keyName,
              sections[i].questions.filter((q) => q.answer === "Yes").length
            );
          }
          const { data } = await fileUpload(`${API.GET_ANALYSIS}`, formData);
          setAnalysis(data.data);
        } catch (err) {
          console.error("Error fetching sections:", err);
          setAnalysis([]);
        } finally {
          setLoadingSummary(false);
        }
      },
      [organizationData?.organizationId, sectionType]
    );

  return (
    <ResponsiveDrawerForm
      description={""}
      title={`${sectionType} Risk Assessment`}
      handleOpen={() => {}}
      handleClose={() => {}}
      className={"md:w-[65vw]"}
      open={showView}
      buttonClassName={""}
      setOpen={setShowView}
      read={true}
    >
      {loading ? (
        <Spin full expand={true} />
      ) : (
        <div className="space-y-6">
          <Card>
            <CardContent className="p-4 flex flex-wrap gap-6 text-sm">
              <div>
                <strong>Year:</strong> {year?.format("YYYY") || "-"}
              </div>
              <div>
                <strong>Assessment:</strong>{" "}
                {assessmentDate?.format("DD-MM-YYYY")}
              </div>
              {/* <div>
                <strong>Review:</strong> {reviewDate?.format("DD-MM-YYYY")}
              </div> */}
              <div>
                <strong>Compliance Manager:</strong>{" "} {compliance ? compliance : ""}
              </div>
              <div>
                <strong>Assessed By:</strong>{" "}
                {assessedBy || localStorage.getItem("FullName")}
              </div>
            </CardContent>
          </Card>

          <Stepper
            initialStep={0}
            steps={stepsData}
            onClickStep={(step, setStep) => setStep(step)}
          >
            {sections?.map((section, index) => (
              <Step
                key={section.id ?? index}
                label={section.title}
                horizontalStepClassName="flex flex-col gap-2"
              >
                <div ref={questionBodyRef} className="space-y-4">
                  {section.sub_title && (
                    <p className="text-sm text-muted-foreground">
                      {section.sub_title}
                    </p>
                  )}

                  {section.questions.map((q) => (
                    <Card
                      key={q.id}
                      className="border"
                      style={{
                        borderColor:
                          q.answer === "Yes"
                            ? section.yes_color
                            : q.answer === "No"
                            ? section.no_color
                            : undefined,
                      }}
                    >
                      <CardHeader className="p-3">
                        <p>
                          <span className="font-medium">{q.label}: </span>
                          {q.question}
                        </p>
                      </CardHeader>
                      <CardContent className="p-3 pt-0">
                        <p className="font-semibold text-sm">
                          Answer: {q.answer}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </Step>
            ))}

            {/* ✅ SUMMARY STEP */}
            <Step label="Summary" horizontalStepClassName="flex flex-col gap-2">
              {loadingSummary ? <Spin expand={true}/> : <div className="flex flex-wrap gap-10">
                <div>
                  <h3 className="font-semibold mb-3">Risk Summary</h3>
                  {yesCounts.map((count, i) => (
                    <p key={i} className="text-sm">
                      Section {i + 1}: {count}/{sections[i]?.questions.length}
                    </p>
                  ))}
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold">
                      Risk Level: {analysis?.risk_level}
                    </h4>
                    <div
                      className="h-4 w-4 rounded-full"
                      style={{ background: analysis?.color }}
                    />
                  </div>

                  <p className="text-sm mb-4">
                    Recommendation: {analysis?.risk_action}
                  </p>

                  <PieChart width={400} height={200}>
                    <Pie
                      data={chartData}
                      startAngle={180}
                      endAngle={0}
                      cx={cx}
                      cy={cy}
                      innerRadius={iR}
                      outerRadius={oR}
                      dataKey="value"
                    >
                      {chartData.map((e, i) => (
                        <Cell key={i} fill={e.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    {needle(needleData[analysis?.risk_level] || 0, chartData)}
                  </PieChart>
                </div>
              </div>}
            </Step>

            {/* ✅ NAVIGATION MUST BE HERE */}
            <StepperNavigation sections={sections} fetchAnalysis={fetchAnalysis}/>
          </Stepper>
        </div>
      )}
    </ResponsiveDrawerForm>
  );
};

export default RiskAssessmentDetailsPopup;
